import React, { useMemo } from "react";
import {
    ResponsiveContainer,
    AreaChart, Area,
    XAxis, YAxis, Tooltip, CartesianGrid,
    BarChart, Bar
} from "recharts";

type Point = { date: string; value: number };

type Props = {
    title?: string;
    periodLabel: string;                 // "Periodo: 2025-01-01 → 2025-12-31"
    totalKg?: number;                    // total del backend (en kg)
    series?: Point[];                    // opcional: serie temporal para sparkline
    loading?: boolean;
    error?: string | null;
    unit?: "kg" | "t";                   // por si quieres cambiar unidad
    height?: number;                     // default 280
    className?: string;
    color?: string;                      // color principal
};

const fmt = (n: number, unit: string) =>
    `${new Intl.NumberFormat("es-ES", { maximumFractionDigits: 2 }).format(n)} ${unit}`;

export const TotalOilCollected: React.FC<Props> = ({
                                                       title = "Aceite recolectado (Kg)",
                                                       periodLabel,
                                                       totalKg = 0,
                                                       series = [],
                                                       loading = false,
                                                       error = null,
                                                       unit = "kg",
                                                       height = 280,
                                                       className = "",
                                                       color = "#d67611", // mismo verde que usas
                                                   }) => {
    const hasSeries = Array.isArray(series) && series.length > 0;
    const noData = (totalKg ?? 0) === 0 && !hasSeries;

    // Si pasas toneladas, convierte antes de pintar (opcional)
    const displayTotal = useMemo(() => totalKg, [totalKg]);


    return (
        <div className={`rounded-2xl border h-full bg-white shadow-sm ${className}`} style={{ minHeight: height }}>
            {/* Header */}
            <div className="p-4 flex items-center justify-between">
                <h3 className="font-semibold">{title}</h3>
                <div className="text-sm text-gray-600">{periodLabel}</div>
            </div>

            <div className="px-4 pb-6 relative">
                {loading ? (
                    <div className="p-6 text-sm text-gray-500">Cargando datos…</div>
                ) : error ? (
                    <div className="p-6 text-sm text-rose-600">{error}</div>
                ) : (
                    <div className={`${noData ? "opacity-40 blur-[2px] pointer-events-none" : ""}`}>
                        {/* Número grande */}

                        {noData ? (
                            <></>
                        ) : (
                            <div className="flex items-end justify-between mb-3">
                                <div className="text-4xl md:text-5xl font-semibold tracking-tight">
                                    {fmt(displayTotal, unit)}
                                </div>
                                {!hasSeries && (
                                    <span className="text-xs text-gray-500">Total del periodo</span>
                                )}
                            </div>
                        )}

                        {/* Gráfico */}
                        <div style={{ height: height - 100 }}>
                            <ResponsiveContainer width="100%" height="100%">
                                {hasSeries ? (
                                    <AreaChart data={series} margin={{ left: 0, right: 0, top: 8, bottom: 0 }}>
                                        <defs>
                                            <linearGradient id="oilGrad" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={color} stopOpacity={0.35} />
                                                <stop offset="100%" stopColor={color} stopOpacity={0.05} />
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" />
                                        <XAxis dataKey="date" tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                                        <YAxis width={50} tickLine={false} axisLine={false} tick={{ fontSize: 11 }} />
                                        <Tooltip
                                            formatter={(v: any) => [fmt(Number(v), unit), "Aceite"]}
                                            labelFormatter={(l) => `Fecha: ${l}`}
                                        />
                                        <Area
                                            type="monotone"
                                            dataKey="value"
                                            stroke={color}
                                            strokeWidth={3}
                                            fill="url(#oilGrad)"
                                            activeDot={{ r: 5 }}
                                        />
                                    </AreaChart>
                                ) : (
                                    <BarChart data={[{ name: "Total", value: displayTotal }]}>
                                        <XAxis dataKey="name" tickLine={false} axisLine={false} />
                                        <YAxis width={50} tickLine={false} axisLine={false} />
                                        <Tooltip formatter={(v: any) => [fmt(Number(v), unit), "Aceite"]} />
                                        <Bar dataKey="value" radius={[8, 8, 0, 0]} fill={color} />
                                    </BarChart>
                                )}
                            </ResponsiveContainer>
                        </div>
                    </div>
                )}

                {/* Empty state consistente */}
                {!(loading || error) && noData && (
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                        <div className="rounded-xl bg-white/80 px-4 py-2 text-sm text-gray-700 shadow">
                            No hay datos para el periodo seleccionado.
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
